/**
 * ═══════════════════════════════════════════════════════════════
 * Apex Vektor — Garage & Fitment Engine
 * ═══════════════════════════════════════════════════════════════
 *
 * FILE:    assets/apex-vektor.js
 * VERSION: 2.0.0
 * STORE:   Apex Vektor Performance
 * COMPAT:  Shopify OS 2.0 · Storefront API 2025-01
 *
 * MODULES:
 *   1. GarageManager    — localStorage + Customer Metafield sync
 *   2. FitmentChecker   — vehicle compatibility logic
 *   3. VehicleSelector  — cascading Make→Model→Chassis UI
 *   4. UIManager        — badges, fitment panel, header display
 *   5. CollectionFilter — S&D URL filter integration
 *
 * SETUP in layout/theme.liquid (before </head>):
 * ────────────────────────────────────────────────
 *   <script>
 *     window.apexVektor = {
 *       storefrontPublicToken: '{{ shop.metafields.apex.storefront_token.value }}',
 *       isCustomer: {{ customer != blank | json }},
 *       customerId: {{ customer.id | default: 'null' | json }},
 *       customerAccessToken: null, // Set via Storefront API login flow
 *       customerGarage: {% if customer and customer.metafields.garage.vehicles != blank %}
 *         {{ customer.metafields.garage.vehicles.value | json }}
 *       {% else %}
 *         null
 *       {% endif %}
 *     };
 *   </script>
 *
 * LOAD before </body>:
 * ────────────────────────────────────────────────
 *   {{ 'apex-vektor.js' | asset_url | script_tag }}
 * ═══════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════

  const CONFIG = {
    LS_KEY:           'apex_vektor_garage',
    LS_CATALOG_KEY:   'apex_vektor_catalog',
    LS_CATALOG_TTL:   86400000, // 24h in ms
    API_VERSION:      '2025-01',
    MAX_GARAGE_SIZE:  10,
    DEBUG:            false,
  };

  const CSS_CLASSES = {
    BADGE_FITS:     'av-badge--fits',
    BADGE_NO_FIT:   'av-badge--no-fit',
    BADGE_NEUTRAL:  'av-badge--neutral',
    BADGE_HIDDEN:   'av-badge--hidden',
    ITEM_FITS:      'av-fitment-item--fits',
    ITEM_MISS:      'av-fitment-item--miss',
    ITEM_ACTIVE:    'av-fitment-item--active',
    STATUS_FITS:    'av-fitment-status--fits',
    STATUS_NO_FIT:  'av-fitment-status--no-fit',
    CARD_PRIMARY:   'av-garage-card--primary',
    LABEL_EMPTY:    'av-garage-label--empty',
  };

  const log = (...args) => CONFIG.DEBUG && console.log('[ApexVektor]', ...args);


  // ═══════════════════════════════════════════════
  // MODULE 1: GARAGE MANAGER
  // Single source of truth for the customer's garage.
  // Guest:      localStorage only
  // Logged-in:  localStorage (cache) + Shopify Customer Metafield (persistent)
  // ═══════════════════════════════════════════════

  const GarageManager = {

    // ── Read ──────────────────────────────────────

    /**
     * Returns the current garage object.
     * Logged-in users: use the server-injected customerGarage (from Liquid).
     * Guests: read from localStorage.
     */
    get() {
      const ctx = window.apexVektor;
      if (ctx?.isCustomer && ctx?.customerGarage) {
        return ctx.customerGarage;
      }
      return this._readLocal();
    },

    getPrimaryId() {
      return this.get()?.primary ?? null;
    },

    getPrimaryVehicle() {
      const garage = this.get();
      if (!garage?.primary) return null;
      return garage.vehicles?.find(v => v.id === garage.primary) ?? null;
    },

    getAllVehicles() {
      return this.get()?.vehicles ?? [];
    },

    hasVehicle(vehicleId) {
      return this.getAllVehicles().some(v => v.id === vehicleId);
    },

    // ── Write ─────────────────────────────────────

    /**
     * Add a vehicle to the garage.
     * @param {Object} vehicleData - Object with id, display_name, make, model, chassis
     * @returns {Object} updated garage
     */
    async addVehicle(vehicleData) {
      const garage = this.get();

      if (!vehicleData?.id) {
        throw new Error('Vehicle data must include an id field');
      }

      // Prevent duplicates
      if (this.hasVehicle(vehicleData.id)) {
        log('Vehicle already in garage:', vehicleData.id);
        return garage;
      }

      // Enforce max garage size
      if (garage.vehicles.length >= CONFIG.MAX_GARAGE_SIZE) {
        throw new Error(`Garage is full. Maximum ${CONFIG.MAX_GARAGE_SIZE} vehicles.`);
      }

      garage.vehicles.push({
        id:           vehicleData.id,
        display_name: vehicleData.display_name,
        make:         vehicleData.make,
        model:        vehicleData.model,
        chassis:      vehicleData.chassis,
        start_year:   vehicleData.start_year,
        end_year:     vehicleData.end_year ?? null,
        added_at:     new Date().toISOString(),
      });

      // First vehicle becomes primary automatically
      if (!garage.primary) {
        garage.primary = vehicleData.id;
      }

      await this._persist(garage);
      log('Vehicle added:', vehicleData.id);
      return garage;
    },

    /**
     * Remove a vehicle from the garage.
     * If the removed vehicle was primary, the next vehicle in the list is promoted.
     */
    async removeVehicle(vehicleId) {
      const garage = this.get();
      const wasCount = garage.vehicles.length;
      garage.vehicles = garage.vehicles.filter(v => v.id !== vehicleId);

      if (garage.vehicles.length === wasCount) {
        log('Vehicle not found in garage:', vehicleId);
        return garage;
      }

      // Promote next vehicle if primary was removed
      if (garage.primary === vehicleId) {
        garage.primary = garage.vehicles[0]?.id ?? null;
      }

      await this._persist(garage);
      log('Vehicle removed:', vehicleId, '| New primary:', garage.primary);
      return garage;
    },

    /**
     * Set a vehicle as primary (active vehicle).
     */
    async setPrimary(vehicleId) {
      const garage = this.get();

      if (!this.hasVehicle(vehicleId)) {
        throw new Error(`Vehicle ${vehicleId} is not in the garage`);
      }

      garage.primary = vehicleId;
      await this._persist(garage);
      log('Primary vehicle set to:', vehicleId);
      return garage;
    },

    /**
     * Clear the entire garage.
     */
    async clear() {
      const empty = this._empty();
      await this._persist(empty);
      log('Garage cleared');
      return empty;
    },

    // ── Sync ──────────────────────────────────────

    /**
     * Merge localStorage garage into Shopify Customer Metafield on login.
     * Call this after a successful customer login event.
     * Vehicles in localStorage not already in the server garage are added.
     * The server garage's primary vehicle takes precedence if already set.
     */
    async mergeOnLogin() {
      if (!window.apexVektor?.isCustomer) return;

      const local = this._readLocal();
      if (!local?.vehicles?.length) {
        log('No local garage to merge');
        return;
      }

      const server = window.apexVektor?.customerGarage ?? this._empty();
      const serverIds = new Set(server.vehicles.map(v => v.id));

      let merged = false;
      local.vehicles.forEach(v => {
        if (!serverIds.has(v.id)) {
          server.vehicles.push(v);
          merged = true;
        }
      });

      if (!server.primary && local.primary) {
        server.primary = local.primary;
      }

      if (merged) {
        await this._persist(server);
        log('Merged local garage into Shopify:', server.vehicles.length, 'vehicles');
      }

      // Clean up localStorage after merge
      try { localStorage.removeItem(CONFIG.LS_KEY); } catch (_) {}
    },

    // ── Internal ──────────────────────────────────

    async _persist(garage) {
      // Always write to localStorage (fast, works offline)
      this._writeLocal(garage);

      // Update in-memory reference
      if (window.apexVektor) {
        window.apexVektor.customerGarage = garage;
      }

      // Sync to Shopify if logged in
      if (window.apexVektor?.isCustomer && window.apexVektor?.customerId) {
        try {
          await this._syncToShopify(garage);
        } catch (err) {
          console.warn('[ApexVektor] Shopify sync failed — data saved locally:', err.message);
        }
      }
    },

    async _syncToShopify(garage) {
      const ctx = window.apexVektor;
      const token = ctx?.customerAccessToken;
      const publicToken = ctx?.storefrontPublicToken;
      const customerId = ctx?.customerId;

      if (!publicToken || !customerId) {
        log('Missing Storefront token or customer ID — skipping Shopify sync');
        return;
      }

      const mutation = `
        mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
          metafieldsSet(metafields: $metafields) {
            metafields { key namespace value }
            userErrors { field message code }
          }
        }
      `;

      const headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': publicToken,
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`/api/${CONFIG.API_VERSION}/graphql.json`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: mutation,
          variables: {
            metafields: [{
              ownerId:   `gid://shopify/Customer/${customerId}`,
              namespace: 'garage',
              key:       'vehicles',
              type:      'json',
              value:     JSON.stringify(garage),
            }]
          }
        })
      });

      const result = await response.json();
      const errors = result.data?.metafieldsSet?.userErrors;
      if (errors?.length) {
        throw new Error(errors.map(e => e.message).join(', '));
      }
      log('Synced garage to Shopify Customer Metafield');
    },

    _readLocal() {
      try {
        const raw = localStorage.getItem(CONFIG.LS_KEY);
        return raw ? JSON.parse(raw) : this._empty();
      } catch {
        return this._empty();
      }
    },

    _writeLocal(garage) {
      try {
        localStorage.setItem(CONFIG.LS_KEY, JSON.stringify(garage));
      } catch (e) {
        console.warn('[ApexVektor] localStorage write failed:', e);
      }
    },

    _empty() {
      return { primary: null, vehicles: [] };
    },
  };


  // ═══════════════════════════════════════════════
  // MODULE 2: FITMENT CHECKER
  // Pure functions — no side effects.
  // ═══════════════════════════════════════════════

  const FitmentChecker = {

    /**
     * Check primary vehicle against a product's compatible ID list.
     * @param {string[]} compatibleIds — vehicle IDs from fitment.compatible_vehicles
     * @returns {'fits'|'no-fit'|'no-vehicle'|'universal'}
     */
    checkPrimary(compatibleIds) {
      if (!compatibleIds?.length) return 'universal';
      const primaryId = GarageManager.getPrimaryId();
      if (!primaryId) return 'no-vehicle';
      return compatibleIds.includes(primaryId) ? 'fits' : 'no-fit';
    },

    /**
     * Check all garage vehicles against compatible IDs.
     * @returns {string[]} array of vehicle IDs from garage that fit
     */
    checkAll(compatibleIds) {
      if (!compatibleIds?.length) return [];
      const vehicles = GarageManager.getAllVehicles();
      return vehicles.filter(v => compatibleIds.includes(v.id)).map(v => v.id);
    },

    /**
     * Parse compatible IDs from a DOM element's data attribute.
     * @param {HTMLElement} el — element with data-compatible-ids="WRX_VA,STI_VA"
     * @returns {string[]}
     */
    parseIdsFromEl(el) {
      const raw = el?.dataset?.compatibleIds ?? '';
      return raw.split(',').map(s => s.trim()).filter(Boolean);
    },

    /**
     * Parse fitment data from the embedded product JSON script tag.
     * @returns {{ product_id: string, compatible_ids: string[] } | null}
     */
    parseProductData() {
      try {
        const el = document.getElementById('av-product-fitment-data');
        return el ? JSON.parse(el.textContent) : null;
      } catch {
        return null;
      }
    },
  };


  // ═══════════════════════════════════════════════
  // MODULE 3: VEHICLE SELECTOR
  // Drives cascading Make → Model → Chassis dropdowns.
  // Works with both the garage modal and any standalone selector.
  // ═══════════════════════════════════════════════

  const VehicleSelector = {

    catalog: [],

    /**
     * Initialize the selector.
     * @param {Object[]} catalog — array of vehicle objects from vehicle-catalog JSON
     */
    init(catalog) {
      this.catalog = catalog ?? [];
      log('Catalog loaded:', this.catalog.length, 'vehicles');

      const makeSelect = document.getElementById('av-select-make');
      if (!makeSelect) return;

      this._populateMakes(makeSelect);
      this._bindEvents();
    },

    // ── Private ───────────────────────────────────

    _populateMakes(makeSelect) {
      const makes = [...new Set(this.catalog.map(v => (v.make || '').trim()))].filter(Boolean).sort();
      makeSelect.innerHTML = '<option value="">Select Make</option>';
      makes.forEach(make => {
        makeSelect.insertAdjacentHTML(
          'beforeend',
          `<option value="${this._esc(make)}">${this._esc(make)}</option>`
        );
      });
      makeSelect.disabled = false;
    },

    _bindEvents() {
      document.getElementById('av-select-make')
        ?.addEventListener('change', () => this._onMakeChange());

      document.getElementById('av-select-model')
        ?.addEventListener('change', () => this._onModelChange());

      document.getElementById('av-select-chassis')
        ?.addEventListener('change', () => this._onChassisChange());

      document.getElementById('av-save-vehicle')
        ?.addEventListener('click', () => this._onSave());

      document.getElementById('av-vehicle-go')
          ?.addEventListener('click', () => this._onGo());

      document.getElementById('av-clear-vehicle')
        ?.addEventListener('click', (e) => {
          e.preventDefault();
          UIManager.openGarage();
        });
    },

  _onMakeChange() {
  const make = document.getElementById('av-select-make').value;
  const modelSel = document.getElementById('av-select-model');
  const chassisSel = document.getElementById('av-select-chassis');
  const saveBtn = document.getElementById('av-save-vehicle');
  const goBtn = document.getElementById('av-vehicle-go');

  this._resetSelect(chassisSel, 'Select Chassis');
  this._resetSelect(modelSel, 'Select Model');
  if (saveBtn) saveBtn.disabled = true;
  if (goBtn) goBtn.disabled = true;

  if (!make) return;

  const matched = this.catalog.filter(
    v => String(v.make || '').trim().toLowerCase() === make.toLowerCase()
  );

  const models = [
    ...new Set(matched.map(v => String(v.model || '').trim()))
  ].filter(Boolean).sort();

  models.forEach(model => {
    modelSel.insertAdjacentHTML(
      'beforeend',
      `<option value="${this._esc(model)}">${this._esc(model)}</option>`
    );
  });
  modelSel.disabled = false;
},

    _onModelChange() {
      const make = document.getElementById('av-select-make').value;
      const model = document.getElementById('av-select-model').value;
      const chassisSel = document.getElementById('av-select-chassis');
      const saveBtn = document.getElementById('av-save-vehicle');
      const goBtn = document.getElementById('av-vehicle-go');

      this._resetSelect(chassisSel, 'Select Chassis');
      if (saveBtn) saveBtn.disabled = true;
      if (goBtn) goBtn.disabled = true;

      if (!model) return;

      const vehicles = this.catalog.filter(v => v.make === make && v.model === model);

      vehicles.forEach(v => {
        const yearRange = v.end_year
          ? `${v.start_year}–${v.end_year}`
          : `${v.start_year}–Present`;
        chassisSel.insertAdjacentHTML(
          'beforeend',
          `<option value="${this._esc(v.id)}">${this._esc(v.chassis)} (${yearRange})</option>`
        );
      });
      chassisSel.disabled = false;
    },

    _onChassisChange() {
      const saveBtn = document.getElementById('av-save-vehicle');
      const goBtn = document.getElementById('av-vehicle-go');
      const chassisVal = document.getElementById('av-select-chassis')?.value;
      if (saveBtn) saveBtn.disabled = !chassisVal;
      if (goBtn) goBtn.disabled = !chassisVal;
    },

    async _onSave() {
      const vehicleId = document.getElementById('av-select-chassis')?.value;
      if (!vehicleId) return;

      const vehicleData = this.catalog.find(v => v.id === vehicleId);
      if (!vehicleData) {
        console.warn('[ApexVektor] Vehicle not found in catalog:', vehicleId);
        return;
      }

      const saveBtn = document.getElementById('av-save-vehicle');
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

      try {
        await GarageManager.addVehicle(vehicleData);
        UIManager.refresh();
        // Reset the selector
        this._resetAll();
        // Close modal if open
        document.getElementById('av-garage-modal')?.setAttribute('hidden', '');
        log('Vehicle saved and UI refreshed');
      } catch (err) {
        console.error('[ApexVektor] Error saving vehicle:', err);
      } finally {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save Vehicle'; }
      }
    },
      _onGo() {
        const vehicleId = document.getElementById('av-select-chassis')?.value;
        if (!vehicleId) return;

        const vehicleData = this.catalog.find(v => v.id === vehicleId);
        if (!vehicleData || !vehicleData.gid) {
          console.warn('[ApexVektor] Vehicle not found in catalog:', vehicleId);
          return;
        }

        if (CollectionFilter._isCollectionPage()) {
          CollectionFilter.applyVehicleFilter(vehicleData);
        } else {
          const url = new URL('/collections/all', window.location.origin);
          url.searchParams.set(CollectionFilter.FILTER_PARAM, vehicleData.gid);
          window.location.href = url.toString();
        }
      },
    _resetAll() {
        ['av-select-make', 'av-select-model', 'av-select-chassis'].forEach(id => {
        const el = document.getElementById(id);
        if (el) {
          el.value = '';
          el.disabled = id !== 'av-select-make';
        }
      });
      const saveBtn = document.getElementById('av-save-vehicle');
      if (saveBtn) saveBtn.disabled = true;
    },

    _resetSelect(selectEl, placeholder) {
      if (!selectEl) return;
      selectEl.innerHTML = `<option value="">${placeholder}</option>`;
      selectEl.disabled = true;
    },

    _esc(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
  };


  // ═══════════════════════════════════════════════
  // MODULE 4: UI MANAGER
  // Manages all DOM updates for fitment state.
  // ═══════════════════════════════════════════════

  const UIManager = {

    init() {
      this._updateGarageDisplay();
      this._updateFitmentBadges();
      this._updateProductFitmentPanel();
      this._renderGarageVehicles();
      this._bindModalEvents();
      this._bindGarageLinks();
      this._updateVehicleSelectorState();
    },

    /**
     * Re-render all fitment UI. Call after any garage mutation.
     */
    refresh() {
      this._updateGarageDisplay();
      this._updateFitmentBadges();
      this._updateProductFitmentPanel();
      this._renderGarageVehicles();
      this._updateVehicleSelectorState();

      // Dispatch event for theme extensions / custom code to hook into
      document.dispatchEvent(new CustomEvent('apex:garage:updated', {
        detail: { garage: GarageManager.get() }
      }));
    },

    openGarage() {
      const modal = document.getElementById('av-garage-modal');
      modal?.removeAttribute('hidden');
      modal?.querySelector('[autofocus]')?.focus();
    },

    closeGarage() {
      document.getElementById('av-garage-modal')?.setAttribute('hidden', '');
    },

    // ── Private: Header Display ───────────────────

_updateGarageDisplay() {
  const primary = GarageManager.getPrimaryVehicle();
  const nameEl = document.getElementById('av-vehicle-name');
  const garageLink = document.getElementById('av-open-garage');
  const selectorBar = document.getElementById('av-selector-bar');

  if (primary) {
    if (selectorBar) selectorBar.style.display = 'none';
    if (nameEl) {
      nameEl.textContent = primary.display_name;
      nameEl.style.display = '';
      nameEl.classList.remove(CSS_CLASSES.LABEL_EMPTY);
    }
    if (garageLink) {
      garageLink.textContent = 'Garage';
      garageLink.style.display = '';
    }
  } else {
    if (selectorBar) selectorBar.style.display = '';
    if (nameEl) nameEl.style.display = 'none';
    if (garageLink) garageLink.style.display = 'none';
  }
},

    _updateVehicleSelectorState() {
      const primary = GarageManager.getPrimaryVehicle();
      const clearBtn = document.getElementById('av-clear-vehicle');
      if (clearBtn) {
        clearBtn.style.display = primary ? 'inline-flex' : 'none';
      }
    },

    // ── Private: Collection Badges ────────────────

    _updateFitmentBadges() {
      document.querySelectorAll('[data-fitment-badge]').forEach(el => {
        const ids = FitmentChecker.parseIdsFromEl(el);
        const state = FitmentChecker.checkPrimary(ids);
        const badge = el.querySelector('.av-badge');
        if (!badge) return;

        // Clear state classes
        Object.values(CSS_CLASSES).forEach(cls => badge.classList.remove(cls));

        switch (state) {
          case 'fits':
            badge.textContent = '✅ Fits Your Vehicle';
            badge.classList.add(CSS_CLASSES.BADGE_FITS);
            break;
          case 'no-fit':
            badge.textContent = '❌ Does Not Fit';
            badge.classList.add(CSS_CLASSES.BADGE_NO_FIT);
            break;
          case 'no-vehicle':
            badge.textContent = 'Select vehicle to check';
            badge.classList.add(CSS_CLASSES.BADGE_NEUTRAL);
            break;
          case 'universal':
            badge.classList.add(CSS_CLASSES.BADGE_HIDDEN);
            break;
        }
      });
    },

    // ── Private: Product Fitment Panel ───────────

    _updateProductFitmentPanel() {
      const fitmentData = FitmentChecker.parseProductData();
      if (!fitmentData) return;

      const { compatible_ids: compatibleIds } = fitmentData;
      const primaryId = GarageManager.getPrimaryId();
      const primaryVehicle = GarageManager.getPrimaryVehicle();
      const statusEl = document.getElementById('av-fitment-status');
      const noVehicleMsg = document.getElementById('av-fitment-no-vehicle');

      // ── Status Banner ──
      if (statusEl) {
        statusEl.classList.remove(CSS_CLASSES.STATUS_FITS, CSS_CLASSES.STATUS_NO_FIT);

        if (!primaryId) {
          statusEl.textContent = '';
          noVehicleMsg?.removeAttribute('style');
        } else if (compatibleIds.includes(primaryId)) {
          statusEl.textContent = `✅ Fits your ${primaryVehicle?.display_name ?? primaryId}`;
          statusEl.classList.add(CSS_CLASSES.STATUS_FITS);
          noVehicleMsg?.style.setProperty('display', 'none');
        } else {
          statusEl.textContent = '❌ Does not fit your vehicle';
          statusEl.classList.add(CSS_CLASSES.STATUS_NO_FIT);
          noVehicleMsg?.style.setProperty('display', 'none');
        }
      }

      // ── Highlight matching items in vehicle list ──
      document.querySelectorAll('[data-fitment-item]').forEach(item => {
        const vehicleId = item.dataset.vehicleId;
        const icon = item.querySelector('.av-fitment-item__icon');
        item.classList.remove(
          CSS_CLASSES.ITEM_FITS,
          CSS_CLASSES.ITEM_MISS,
          CSS_CLASSES.ITEM_ACTIVE
        );

        if (!primaryId) {
          if (icon) icon.textContent = '—';
          return;
        }

        if (vehicleId === primaryId) {
          item.classList.add(CSS_CLASSES.ITEM_FITS, CSS_CLASSES.ITEM_ACTIVE);
          if (icon) icon.textContent = '✅';
        } else {
          item.classList.add(CSS_CLASSES.ITEM_MISS);
          if (icon) icon.textContent = '—';
        }
      });
    },

    // ── Private: Garage Vehicle Cards ────────────

    _renderGarageVehicles() {
      const container = document.getElementById('av-garage-vehicles');
      if (!container) return;

      const emptyMsg = document.getElementById('av-garage-empty');
      const garage = GarageManager.get();

      // Remove existing cards (not the empty message)
      container.querySelectorAll('.av-garage-card').forEach(el => el.remove());

      if (!garage.vehicles?.length) {
        if (emptyMsg) emptyMsg.style.display = 'block';
        return;
      }
      if (emptyMsg) emptyMsg.style.display = 'none';

      const fragment = document.createDocumentFragment();

      garage.vehicles.forEach(vehicle => {
        const isPrimary = vehicle.id === garage.primary;
        const card = this._createVehicleCard(vehicle, isPrimary);
        fragment.appendChild(card);
      });

      container.appendChild(fragment);
    },

    _createVehicleCard(vehicle, isPrimary) {
      const card = document.createElement('div');
      card.className = `av-garage-card${isPrimary ? ` ${CSS_CLASSES.CARD_PRIMARY}` : ''}`;
      card.dataset.vehicleId = vehicle.id;

      const yearRange = vehicle.end_year
        ? `${vehicle.start_year}–${vehicle.end_year}`
        : `${vehicle.start_year}–Present`;

      card.innerHTML = `
  <div class="av-garage-card__info">
    <span class="av-garage-card__name">${this._esc(vehicle.display_name)}</span>
    <span class="av-garage-card__year">${this._esc(yearRange)}</span>
  </div>
  <div class="av-garage-card__actions">
    ${isPrimary
      ? `<span class="av-garage-card__badge">Primary</span>`
      : `<button class="av-btn av-btn--sm" data-action="set-primary" data-vehicle-id="${vehicle.id}">Set Primary</button>`
    }
    <button class="av-btn av-btn--sm av-btn--danger" data-action="remove" data-vehicle-id="${vehicle.id}">Remove</button>
  </div>
`;

      // Bind actions
      card.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const { action, vehicleId } = btn.dataset;
          btn.disabled = true;

          try {
            if (action === 'set-primary') {
              await GarageManager.setPrimary(vehicleId);
            } else if (action === 'remove') {
              await GarageManager.removeVehicle(vehicleId);
            }
            this.refresh();
          } catch (err) {
            console.error('[ApexVektor] Garage action failed:', err);
          } finally {
            btn.disabled = false;
          }
        });
      });

      return card;
    },

    // ── Private: Modal & Links ────────────────────

    _bindModalEvents() {
      document.getElementById('av-modal-close')
        ?.addEventListener('click', () => this.closeGarage());

      document.getElementById('av-modal-overlay')
        ?.addEventListener('click', () => this.closeGarage());

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeGarage();
      });
    },

    _bindGarageLinks() {
      document.querySelectorAll('[id="av-open-garage"], [data-open-garage]').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.openGarage();
        });
      });
    },

    _esc(str) {
      const div = document.createElement('div');
      div.appendChild(document.createTextNode(String(str ?? '')));
      return div.innerHTML;
    },
  };


  // ═══════════════════════════════════════════════
  // MODULE 5: COLLECTION FILTER
  // Integrates with Shopify Search & Discovery URL filters.
  // ═══════════════════════════════════════════════

  const CollectionFilter = {

  FILTER_PARAM: 'filter.p.m.custom.compatible_vehicles',

  init() {
    if (!this._isCollectionPage()) return;

    const primaryVehicle = GarageManager.getPrimaryVehicle();
    const currentFilter = this.getActiveFilterValue();

    if (!primaryVehicle) return;
    if (currentFilter) return;

    // If user came from a filtered page on this site, they removed the filter — respect it
    if (document.referrer.includes(window.location.hostname) &&
        document.referrer.includes(this.FILTER_PARAM)) return;

    this.applyVehicleFilter(primaryVehicle);
  },

  applyVehicleFilter(vehicle) {
    let gid = vehicle.gid;
    if (!gid) {
      const match = VehicleSelector.catalog.find(v => v.id === vehicle.id);
      gid = match?.gid;
    }
    if (!gid) {
      console.warn('[ApexVektor] No GID found for vehicle:', vehicle.id);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(this.FILTER_PARAM, gid);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  },

  getActiveFilterValue() {
    return new URLSearchParams(window.location.search).get(this.FILTER_PARAM);
  },

  _isCollectionPage() {
    return window.location.pathname.includes('/collections/');
  },
};


  // ═══════════════════════════════════════════════
  // CATALOG LOADER
  // Loads vehicle catalog from embedded JSON or sessionStorage cache.
  // ═══════════════════════════════════════════════

  const CatalogLoader = {

    load() {
      // Try embedded JSON first (fastest)
      const embedded = this._loadEmbedded();
      if (embedded?.length) return embedded;

      // Try sessionStorage cache
      const cached = this._loadCached();
      if (cached?.length) return cached;

      return [];
    },

    _loadEmbedded() {
      try {
        const el = document.getElementById('av-vehicle-catalog');
        if (!el) return null;
        const data = JSON.parse(el.textContent);
        // Cache for this session
        this._writeCache(data);
        log('Catalog loaded from embedded JSON:', data.length, 'vehicles');
        return data;
      } catch (e) {
        console.warn('[ApexVektor] Could not parse embedded catalog:', e);
        return null;
      }
    },

    _loadCached() {
      try {
        const raw = sessionStorage.getItem(CONFIG.LS_CATALOG_KEY);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > CONFIG.LS_CATALOG_TTL) {
          sessionStorage.removeItem(CONFIG.LS_CATALOG_KEY);
          return null;
        }
        log('Catalog loaded from sessionStorage cache:', data.length, 'vehicles');
        return data;
      } catch {
        return null;
      }
    },

    _writeCache(data) {
      try {
        sessionStorage.setItem(CONFIG.LS_CATALOG_KEY, JSON.stringify({
          data,
          ts: Date.now()
        }));
      } catch (_) {}
    },
  };


  // ═══════════════════════════════════════════════
  // BOOTSTRAP
  // ═══════════════════════════════════════════════

  async function init() {
    log('Initializing Apex Vektor Fitment System');

    // Load vehicle catalog
    const catalog = CatalogLoader.load();

    // Merge localStorage → Shopify on first login
    if (window.apexVektor?.isCustomer) {
      try {
        await GarageManager.mergeOnLogin();
      } catch (err) {
        console.warn('[ApexVektor] mergeOnLogin failed:', err);
      }
    }

    // Boot modules
    VehicleSelector.init(catalog);
    UIManager.init();
    CollectionFilter.init();

    log('Apex Vektor ready. Primary vehicle:', GarageManager.getPrimaryId() ?? 'none');

    // Signal readiness
    document.dispatchEvent(new CustomEvent('apex:ready', {
      detail: {
        garage: GarageManager.get(),
        catalogSize: catalog.length,
      }
    }));
  }

  // ── Public API ────────────────────────────────
  window.ApexVektor = {
    GarageManager,
    FitmentChecker,
    VehicleSelector,
    UIManager,
    CollectionFilter,

    // Convenience shortcuts
    addVehicle:    (v) => GarageManager.addVehicle(v).then(() => UIManager.refresh()),
    removeVehicle: (id) => GarageManager.removeVehicle(id).then(() => UIManager.refresh()),
    setPrimary:    (id) => GarageManager.setPrimary(id).then(() => UIManager.refresh()),
    clearGarage:   () => GarageManager.clear().then(() => UIManager.refresh()),
    checkFitment:  (ids) => FitmentChecker.checkPrimary(ids),
  };

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();


// ═══════════════════════════════════════════════════════════════
// USAGE EXAMPLES (for reference — not executed)
// ═══════════════════════════════════════════════════════════════

/*
// Listen for garage updates
document.addEventListener('apex:garage:updated', (e) => {
  console.log('Garage updated:', e.detail.garage);
});

// Listen for system ready
document.addEventListener('apex:ready', (e) => {
  console.log('Apex Vektor ready. Vehicles in catalog:', e.detail.catalogSize);
});

// Programmatically add a vehicle
ApexVektor.addVehicle({
  id: 'WRX_VA',
  display_name: '2015–2021 Subaru WRX (VA)',
  make: 'Subaru',
  model: 'WRX',
  chassis: 'VA',
  start_year: 2015,
  end_year: 2021
});

// Check if a product fits (pass vehicle IDs from the product metafield)
const fits = ApexVektor.checkFitment(['WRX_VA', 'STI_VA']);
// Returns: 'fits' | 'no-fit' | 'no-vehicle' | 'universal'

// Apply collection filter for current vehicle
ApexVektor.CollectionFilter.applyVehicleFilter('2015–2021 Subaru WRX (VA)');

// Get current garage state
const garage = ApexVektor.GarageManager.get();
// Returns: { primary: 'WRX_VA', vehicles: [...] }
*/
